import { useState, useEffect, useRef } from 'react';
import {
  Check,
  X,
  IndianRupee,
  Phone,
  Clock,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Truck,
  Eye,
  FileSpreadsheet,
  FileText,
  Printer,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { orderAPI } from '@/lib/api';

const DELIVERY_STATUSES = [
  { value: 'processing', label: 'Processing' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 're_dispatched', label: 'Re-dispatched' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'returned', label: 'Return Received' },
  { value: 'refunding', label: 'Refund Initiated' },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Dialogs
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [deliveryDescription, setDeliveryDescription] = useState('');
  const [processing, setProcessing] = useState(false);

  // ===== PRINT (No popup blocker) =====
  const printFrameRef = useRef(null);

  const escapeHtml = (str = '') =>
    String(str).replace(/[&<>"']/g, (m) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }[m]));

  const buildPrintHtml = (order) => {
    const itemsRows = (order.items || [])
      .map(
        (item) => `
          <tr>
            <td>${escapeHtml(item.product_name)} × ${escapeHtml(item.quantity)}</td>
            <td style="text-align:right;">₹${escapeHtml(item.subtotal)}</td>
          </tr>
        `
      )
      .join('');

    const createdAt = order.created_at
      ? new Date(order.created_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Order ${escapeHtml(order.id?.substring(0, 8) || '')}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
            h2 { margin: 0 0 6px; }
            .muted { color:#666; font-size: 12px; margin-bottom: 10px; }
            .box { border:1px solid #eee; border-radius: 12px; padding: 16px; margin-top: 12px; }
            .row { display:flex; justify-content:space-between; gap:16px; margin-top: 10px; }
            .hr { height:1px; background:#eee; margin: 14px 0; }
            table { width:100%; border-collapse: collapse; margin-top: 6px; }
            td { padding: 8px 0; border-bottom: 1px solid #eee; vertical-align: top; }
            .total { font-weight: 700; font-size: 16px; }
            @media print {
              body { padding: 0; }
              .box { border: none; padding: 0; }
            }
          </style>
        </head>
        <body>
          <h2>Order Details</h2>
          <div class="muted">
            #${escapeHtml(order.id?.substring(0, 8) || '')}
            ${createdAt ? ` • ${escapeHtml(createdAt)}` : ''}
          </div>

          <div class="box">
            <div class="row">
              <div>
                <div class="muted" style="margin:0;">Customer</div>
                <div><b>${escapeHtml(order.customer_name || '')}</b></div>
              </div>
              <div style="text-align:right;">
                <div class="muted" style="margin:0;">Phone</div>
                <div><b>${escapeHtml(order.customer_phone || '')}</b></div>
              </div>
            </div>

            <div style="margin-top:10px;">
              <div class="muted" style="margin:0;">Address</div>
              <div><b>${escapeHtml(order.customer_address || '')}</b></div>
            </div>

            <div class="hr"></div>

            <div>
              <div class="muted" style="margin:0 0 6px;">Items</div>
              <table>
                ${itemsRows || '<tr><td>No items</td><td></td></tr>'}
              </table>

              <div class="row" style="margin-top:12px;">
                <div class="total">Total (Including Delivery)</div>
                <div class="total">₹${escapeHtml(order.total_amount)}</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrintOrder = () => {
    if (!selectedOrder) return;

    const iframe = printFrameRef.current;
    if (!iframe || !iframe.contentWindow) {
      toast.error('Print frame not available. Please try again.');
      return;
    }

    const doc = iframe.contentWindow.document;
    const html = buildPrintHtml(selectedOrder);

    doc.open();
    doc.write(html);
    doc.close();

    // Give browser a moment to layout the iframe content
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        toast.error('Print failed. Please try again.');
      }
    }, 250);
  };
  // ===== /PRINT =====

  useEffect(() => {
    fetchOrders();
  }, [page, search, statusFilter, sortBy, sortOrder]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await orderAPI.getAll({
        page,
        page_size: pageSize,
        search: search || undefined,
        status_filter: statusFilter !== 'all' ? statusFilter : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setOrders(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (order) => {
    setProcessing(true);
    try {
      await orderAPI.action(order.id, { action: 'accept' });
      toast.success('Order accepted');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to accept order');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await orderAPI.action(selectedOrder.id, {
        action: 'reject',
        rejection_reason: rejectionReason,
      });
      toast.success('Order rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to reject order');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateDelivery = async () => {
    if (!deliveryStatus) {
      toast.error('Please select a delivery status');
      return;
    }

    setProcessing(true);
    try {
      await orderAPI.updateDelivery(selectedOrder.id, {
        delivery_status: deliveryStatus,
        delivery_description: deliveryDescription,
      });
      toast.success('Delivery status updated');
      setDeliveryDialogOpen(false);
      setDeliveryStatus('');
      setDeliveryDescription('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery status');
    } finally {
      setProcessing(false);
    }
  };

  const getExportData = () => {
    const headers = [
      'Order ID',
      'Customer',
      'Phone',
      'Items',
      'Total (₹)',
      'Payment',
      'Status',
      'Delivery',
      'Date',
    ];
    const rows = orders.map((o) => [
      o.id.substring(0, 8),
      o.customer_name,
      o.customer_phone,
      o.items.map((i) => `${i.product_name}x${i.quantity}`).join('; '),
      o.total_amount,
      o.payment_method.toUpperCase(),
      o.status,
      o.delivery_status,
      new Date(o.created_at).toLocaleDateString(),
    ]);
    return { headers, rows };
  };

  const exportToCSV = () => {
    const { headers, rows } = getExportData();
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Orders exported to CSV');
  };

  const exportToExcel = () => {
    const { headers, rows } = getExportData();
    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');

    const colWidths = headers.map((h, i) => ({
      wch: Math.max(h.length, ...rows.map((r) => String(r[i]).length)) + 2,
    }));
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `orders-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Orders exported to Excel');
  };

  const exportToPDF = () => {
    const { headers, rows } = getExportData();
    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(16);
    doc.text('Orders Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 28,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [249, 115, 22], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 20 },
        4: { halign: 'right' },
      },
    });

    doc.save(`orders-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Orders exported to PDF');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200"
          >
            Pending
          </Badge>
        );
      case 'accepted':
        return <Badge className="bg-secondary text-secondary-foreground">Accepted</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDeliveryBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      processing: { label: 'Processing', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      dispatched: { label: 'Dispatched', className: 'bg-purple-50 text-purple-700 border-purple-200' },
      re_dispatched: { label: 'Re-dispatched', className: 'bg-purple-50 text-purple-700 border-purple-200' },
      delivered: { label: 'Delivered', className: 'bg-green-50 text-green-700 border-green-200' },
      cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200' },
      returned: { label: 'Return Received', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      refunding: { label: 'Refund Initiated', className: 'bg-orange-50 text-orange-700 border-orange-200' },
    };
    const config = statusMap[status] || statusMap.pending;
    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Orders</h1>
          <p className="text-muted-foreground mt-1">{total} total orders</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="export-btn">
              <Download className="h-4 w-4" /> Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" /> Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportToExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export as Excel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportToPDF}>
              <FileText className="h-4 w-4 mr-2" /> Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or order ID..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={sortOrder}
              onValueChange={(v) => {
                setSortOrder(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest First</SelectItem>
                <SelectItem value="asc">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : orders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">No orders found</h3>
            <p className="text-muted-foreground text-sm">
              {search || statusFilter !== 'all'
                ? 'Try different filters'
                : 'Orders will appear here when customers place them'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={order.id}
                      className={
                        order.status === 'accepted'
                          ? 'bg-green-50/50'
                          : order.status === 'rejected'
                          ? 'bg-red-50/30'
                          : ''
                      }
                      data-testid={`order-row-${order.id}`}
                    >
                      <TableCell>
                        <span className="font-mono text-sm">#{order.id.substring(0, 8)}</span>
                      </TableCell>

                      <TableCell>
                        <div>
                          <p className="font-medium">{order.customer_name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {order.customer_phone}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="max-w-32">
                          {order.items.slice(0, 2).map((item, idx) => (
                            <p key={idx} className="text-sm truncate">
                              {item.product_name} ×{item.quantity}
                            </p>
                          ))}
                          {order.items.length > 2 && (
                            <p className="text-xs text-muted-foreground">
                              +{order.items.length - 2} more
                            </p>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <span className="font-heading font-bold flex items-center">
                          <IndianRupee className="h-3 w-3" />
                          {order.total_amount}
                        </span>
                      </TableCell>

                      <TableCell>
                        <Badge variant="outline" className="uppercase text-xs">
                          {order.payment_method}
                        </Badge>
                        {order.payment_method === 'upi' && order.customer_upi_id && (
                          <p className="text-xs text-muted-foreground mt-1 font-mono">
                            {order.customer_upi_id}
                          </p>
                        )}
                      </TableCell>

                      <TableCell>
                        {getStatusBadge(order.status)}
                        {order.rejection_reason && (
                          <p
                            className="text-xs text-destructive mt-1 max-w-24 truncate"
                            title={order.rejection_reason}
                          >
                            {order.rejection_reason}
                          </p>
                        )}
                      </TableCell>

                      <TableCell>{getDeliveryBadge(order.delivery_status)}</TableCell>

                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(order.created_at)}
                      </TableCell>

                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => {
                              setSelectedOrder(order);
                              setDetailsDialogOpen(true);
                            }}
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {order.status === 'pending' && (
                            <>
                              <Button
                                size="icon"
                                className="h-8 w-8 bg-secondary hover:bg-secondary/90"
                                onClick={() => handleAccept(order)}
                                disabled={processing}
                                data-testid={`accept-order-${order.id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>

                              <Button
                                size="icon"
                                variant="destructive"
                                className="h-8 w-8"
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setRejectDialogOpen(true);
                                }}
                                disabled={processing}
                                data-testid={`reject-order-${order.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}

                          {order.status === 'accepted' && (
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedOrder(order);
                                setDeliveryStatus(order.delivery_status);
                                setDeliveryDescription('');
                                setDeliveryDialogOpen(true);
                              }}
                              data-testid={`delivery-order-${order.id}`}
                            >
                              <Truck className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Order Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto pr-2">
          <DialogHeader>
            <DialogTitle className="font-heading">Order Details</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono">#{selectedOrder.id.substring(0, 8)}</span>
                {getStatusBadge(selectedOrder.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedOrder.customer_phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Address</p>
                  <p className="font-medium">{selectedOrder.customer_address}</p>
                </div>
              </div>

              <div>
                <p className="text-muted-foreground text-sm mb-2">Items</p>
                <div className="bg-muted rounded-lg p-3 space-y-2">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>
                        {item.product_name} × {item.quantity}
                      </span>
                      <span>₹{item.subtotal}</span>
                    </div>
                  ))}
				  <div className="flex justify-between border-t border-border pt-2">
                    <span>Delivery</span>
                    <span>₹{selectedOrder.delivery_charge || 0}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-border pt-2">
                    <span>Total</span>
                    <span>₹{selectedOrder.total_amount}</span>
                  </div>
                </div>
              </div>
			  
			{/* Payment Status */}
			<div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between text-sm">
			  <span className="text-muted-foreground">Payment Status</span>

			  {selectedOrder.payment_method?.toLowerCase() === 'upi' ? (
				<Badge className="bg-green-50 text-green-700 border border-green-200" variant="outline">
				  Paid
				</Badge>
			  ) : (
				<Badge className="bg-amber-50 text-amber-700 border border-amber-200" variant="outline">
				  Cash On Delivery
				</Badge>
			  )}
			</div>

              {/* Print button - center - before Delivery History */}
              <div className="flex justify-center my-2">
                <Button type="button" variant="outline" onClick={handlePrintOrder}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>

              {selectedOrder.delivery_history && selectedOrder.delivery_history.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Delivery History</p>
                  <div className="space-y-2">
                    {selectedOrder.delivery_history.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
                        <div>
                          <p className="font-medium capitalize">
                            {entry.status.replace('_', '-')}
                          </p>
                          {entry.description && (
                            <p className="text-muted-foreground">{entry.description}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Reject Order</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason *</Label>
            <Textarea
              id="reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this order"
              rows={4}
              className="mt-2"
              data-testid="rejection-reason-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This reason will be sent to the customer via email.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing}
              data-testid="confirm-reject-btn"
            >
              {processing ? 'Processing...' : 'Reject Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Status Dialog */}
      <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Update Delivery Status</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Delivery Status</Label>
              <Select value={deliveryStatus} onValueChange={setDeliveryStatus}>
                <SelectTrigger data-testid="delivery-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="delivery-desc">Notes (Optional)</Label>
              <Textarea
                id="delivery-desc"
                value={deliveryDescription}
                onChange={(e) => setDeliveryDescription(e.target.value)}
                placeholder="e.g., Dispatched via local courier, tracking: ABC123"
                rows={3}
                data-testid="delivery-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliveryDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDelivery}
              disabled={processing}
              data-testid="update-delivery-btn"
            >
              {processing ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden iframe for printing - avoids popup blocker */}
      <iframe
        ref={printFrameRef}
        title="print-frame"
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          border: 0,
          left: '-9999px',
          top: '-9999px',
        }}
      />
    </div>
  );
}
